import { serializeError } from "serialize-error";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  supabase,
  supabaseBaseQuery,
  type ModelRequestsFeature,
  type CollapsingAppSidebarFeature,
  type PaidAdsFeature,
  type PremiumModelsFeature,
} from "../../../supabase";
import { features } from "../constants/features";
import { plans } from "../constants/plans";
import type { ValueOf } from "../../../types/utility/ValueOf";

const featuresApi = createApi({
  reducerPath: "api.Features",
  baseQuery: supabaseBaseQuery,
  tagTypes: [
    "UserFeatures",
    "RequestsFeature",
    "CollapsingAppSidebarFeature",
    "PaidAdsFeature",
    "PremiumModelsFeature",
    "CurrentPlan",
  ],
  endpoints: (build) => ({
    getRequestsFeature: build.query<ModelRequestsFeature, void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("user_features")
          .select("feature_slug, preferences, created_at, updated_at")
          .eq("feature_slug", features.MODEL_REQUESTS)
          .single();

        if (error) return { error: serializeError(error) };

        return { data } as { data: ModelRequestsFeature };
      },
      providesTags: ["UserFeatures", "RequestsFeature"],
    }),

    getCollapsingAppSidebarFeature: build.query<CollapsingAppSidebarFeature, void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("user_features")
          .select("feature_slug, preferences, created_at, updated_at")
          .eq("feature_slug", features.ALLOW_COLLAPSING_APP_SIDEBAR)
          .single();

        if (error) return { error: serializeError(error) };

        return { data } as { data: CollapsingAppSidebarFeature };
      },
      providesTags: ["UserFeatures", "CollapsingAppSidebarFeature"],
    }),

    getPaidAdsFeature: build.query<PaidAdsFeature, void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("user_features")
          .select("feature_slug, preferences, created_at, updated_at")
          .eq("feature_slug", features.DISPLAY_PAID_ADS)
          .single();

        if (error) return { error: serializeError(error) };

        return { data } as { data: PaidAdsFeature };
      },
      providesTags: ["UserFeatures", "PaidAdsFeature"],
    }),

    getPremiumModelsFeature: build.query<PremiumModelsFeature, void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("user_features")
          .select("feature_slug, preferences, created_at, updated_at")
          .eq("feature_slug", features.ALLOW_ACCESS_TO_PREMIUM_MODELS)
          .single();

        if (error) return { error: serializeError(error) };

        return { data } as {
          data: PremiumModelsFeature;
        };
      },
      providesTags: ["UserFeatures", "PremiumModelsFeature"],
    }),

    getCurrentPlan: build.query<ValueOf<typeof plans>, void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("user_features")
          .select("feature_slug, preferences, created_at, updated_at")
          .eq("feature_slug", features.ALLOW_ACCESS_TO_PREMIUM_MODELS)
          .single();

        if (error) return { error: serializeError(error) };

        const premiumModelsFeature = data as PremiumModelsFeature;
        const { enabled } = premiumModelsFeature.preferences;

        return {
          data: enabled ? plans.PRO : plans.FREE,
        };
      },
      providesTags: ["UserFeatures", "CurrentPlan"],
    }),
  }),
});

const {
  useGetPaidAdsFeatureQuery,
  useGetPremiumModelsFeatureQuery,
  useGetCollapsingAppSidebarFeatureQuery,
  useGetRequestsFeatureQuery,
  useGetCurrentPlanQuery,
} = featuresApi;

export {
  featuresApi,
  useGetPaidAdsFeatureQuery,
  useGetPremiumModelsFeatureQuery,
  useGetCollapsingAppSidebarFeatureQuery,
  useGetRequestsFeatureQuery,
  useGetCurrentPlanQuery,
};
